import { ShieldCheck, ShieldOff } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import type { AccountApi } from "@hostdock/sdk";
import { Badge } from "../badge";
import { Button } from "../button";
import { Dialog } from "../dialog";
import { FormError, Notice, Spinner } from "../feedback";
import { Field, Input } from "../form";
import { CopyButton } from "../secret";

function RecoveryCodesView({ codes, onDone }: { codes: string[]; onDone(): void }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <Notice tone="warning" title="Save your recovery codes">
        Each code works once if you lose access to your authenticator. They will not be shown again.
      </Notice>
      <div className="grid grid-cols-2 gap-2 rounded-md border border-line bg-surface-2 p-3 font-mono text-[13px]">
        {codes.map((c) => (
          <span key={c} className="select-all">{c}</span>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <CopyButton value={codes.join("\n")} label="Copy codes" />
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="accent-[var(--hd-accent)]" />
          I have saved these codes
        </label>
      </div>
      <Button variant="primary" disabled={!saved} onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

/** Full TOTP enrollment: secret + QR, code confirmation, recovery codes. */
export function MfaEnrollment<TSession>({ api, onDone }: { api: AccountApi<TSession>; onDone(): void }) {
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const setup = useMutation({ meta: { inlineError: true }, mutationFn: () => api.mfaSetup() });
  const confirm = useMutation({ meta: { inlineError: true }, mutationFn: () => api.mfaConfirm(code.trim()) });
  const { mutate: startSetup } = setup;
  const started = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation creating two pending secrets.
    if (started.current) return;
    started.current = true;
    startSetup();
  }, [startSetup]);

  useEffect(() => {
    if (!setup.data) return;
    // QR is rendered locally; the secret never leaves the browser to a third party.
    QRCode.toDataURL(setup.data.otpauthUrl, { margin: 1, width: 176 }).then(setQr).catch(() => setQr(null));
  }, [setup.data]);

  if (confirm.data) return <RecoveryCodesView codes={confirm.data.recoveryCodes} onDone={onDone} />;
  if (setup.isPending || (!setup.data && !setup.error)) return <Spinner label="Preparing two-factor setup" />;
  if (setup.error) {
    return (
      <div className="flex flex-col gap-3">
        <FormError error={setup.error} />
        <Button onClick={() => setup.mutate()}>Try again</Button>
      </div>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    confirm.mutate();
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <ol className="flex list-decimal flex-col gap-1 pl-4 text-[13px] text-muted">
        <li>Open an authenticator app (1Password, Authy, Google Authenticator…).</li>
        <li>Scan the QR code or enter the setup key manually.</li>
        <li>Enter the 6-digit code the app shows.</li>
      </ol>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        {qr ? <img src={qr} width={176} height={176} alt="Two-factor QR code" className="rounded-md border border-line bg-white p-1" /> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[12.5px] font-medium text-muted">Setup key</span>
          <code className="rounded-md border border-line bg-surface-2 px-2 py-1.5 font-mono text-[12.5px] break-all select-all">{setup.data!.secret}</code>
          <CopyButton value={setup.data!.secret} label="Copy key" />
        </div>
      </div>
      <Field label="Authentication code">
        {(p) => (
          <Input {...p} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123456" className="font-mono tracking-widest" />
        )}
      </Field>
      <FormError error={confirm.error} />
      <Button type="submit" variant="primary" loading={confirm.isPending} disabled={!/^\d{6}$/.test(code.trim())}>
        Verify and enable
      </Button>
    </form>
  );
}

export function TwoFactorPanel<TSession>({ api, enabled, required, onChanged }: { api: AccountApi<TSession>; enabled: boolean; required: boolean; onChanged(): void }) {
  const [mode, setMode] = useState<null | "enroll" | "disable" | "codes">(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const disable = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.mfaDisable({ password, code: code.trim() }),
    onSuccess: () => {
      close();
      onChanged();
    },
  });
  const regen = useMutation({ meta: { inlineError: true }, mutationFn: () => api.regenerateRecoveryCodes(password) });
  const close = () => {
    setMode(null);
    setPassword("");
    setCode("");
    disable.reset();
    regen.reset();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {enabled ? <ShieldCheck size={20} className="text-success" /> : <ShieldOff size={20} className="text-warning" />}
          <div>
            <p className="text-sm font-medium">Authenticator app</p>
            <p className="text-[12.5px] text-muted">{enabled ? "A code is required each time you sign in." : "Protect your account with a second step at sign-in."}</p>
          </div>
          <Badge tone={enabled ? "success" : "warning"}>{enabled ? "Enabled" : "Not enabled"}</Badge>
        </div>
        <div className="flex gap-2">
          {enabled ? (
            <>
              <Button size="sm" onClick={() => setMode("codes")}>New recovery codes</Button>
              {!required && (
                <Button size="sm" variant="danger-ghost" onClick={() => setMode("disable")}>
                  Disable
                </Button>
              )}
            </>
          ) : (
            <Button size="sm" variant="primary" onClick={() => setMode("enroll")}>
              Enable two-factor
            </Button>
          )}
        </div>
      </div>
      {required && enabled && <p className="text-[12.5px] text-subtle">Two-factor authentication is required by the security policy and cannot be disabled.</p>}

      <Dialog open={mode === "enroll"} onOpenChange={(o) => !o && close()} title="Enable two-factor authentication">
        {mode === "enroll" && (
          <MfaEnrollment
            api={api}
            onDone={() => {
              close();
              onChanged();
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={mode === "disable"}
        onOpenChange={(o) => !o && close()}
        title="Disable two-factor authentication"
        description="Your account will be protected by your password only."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button variant="danger" loading={disable.isPending} disabled={!password || !code.trim()} onClick={() => disable.mutate()}>
              Disable
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Password">{(p) => <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />}</Field>
          <Field label="Authentication or recovery code">{(p) => <Input {...p} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" />}</Field>
          <FormError error={disable.error} />
        </div>
      </Dialog>

      <Dialog open={mode === "codes"} onOpenChange={(o) => !o && close()} title="Generate new recovery codes" description="Existing recovery codes stop working." size="sm">
        {regen.data ? (
          <RecoveryCodesView codes={regen.data.recoveryCodes} onDone={close} />
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              regen.mutate();
            }}
          >
            <Field label="Confirm your password">{(p) => <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />}</Field>
            <FormError error={regen.error} />
            <Button type="submit" variant="primary" loading={regen.isPending} disabled={!password}>
              Generate codes
            </Button>
          </form>
        )}
      </Dialog>
    </div>
  );
}
